import type { OptionChangeId } from '#app/store/builders/configChangeTracker'
import type { LocalConfigurationId, TruckConfiguration } from '#app/store/slices/configurationSlice'
import type { CheckTaskId } from '#app/types/ConfigurationCheck'
import type { ManCodeWithSortOrder } from '#pages/Configuration/components/ConfigurationNavigation'
import { composeRoute } from '../lib'

const configId = (configuration: TruckConfiguration | LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) => {
  if (manCode) {
    return `${manCode.manCode}:${manCode.sortOrder}` as const
  }

  if (typeof configuration !== 'string') {
    if (configuration.apiConfiguration?.manCode) {
      return `${configuration.apiConfiguration.manCode}:${configuration.sortOrder}` as const
    }
    return configuration.id
  }
  return configuration
}

type ConfigurationParams = {
  configuration: TruckConfiguration | LocalConfigurationId
  manCode?: ManCodeWithSortOrder
}
export const configurationRoutes = [
  // new config
  composeRoute({
    id: 'configuration/new',
    route: '/configuration',
    make: () => '/configuration',
  }),
  // don't know where I came from config
  composeRoute({
    id: 'configuration/redirect',
    route: '/configuration/:configurationId',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}` as const,
  }),
  // error page
  composeRoute({
    id: 'configuration/error',
    route: '/configuration/:configurationId/error',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/error` as const,
  }),
  // copy configuration
  composeRoute({
    id: 'configuration/copy',
    route: '/configuration/:configurationId/copy',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/copy` as const,
  }),

  // step-0: creating a configuration
  composeRoute({
    id: 'configuration/step-0',
    route: '/configuration/:configurationId/step/0',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/0` as const,
  }),
  composeRoute({
    id: 'configuration/step-0/history',
    route: '/configuration/:configurationId/step/0/history',
    make: ({ configuration, manCode, processId }: ConfigurationParams & { processId?: OptionChangeId }) =>
      processId
        ? (`/configuration/${configId(configuration, manCode)}/step/0/history/${processId}` as const)
        : (`/configuration/${configId(configuration, manCode)}/step/0/history` as const),
  }),
  composeRoute({
    id: 'configuration/step-0/landing',
    route: '/configuration/:configurationId/step/0/landing',
    make: ({ configuration, manCode }: ConfigurationParams) => `configuration/${configId(configuration, manCode)}/step/0/landing` as const,
  }),

  // step-1
  composeRoute({
    id: 'configuration/step-1',
    route: '/configuration/:configurationId/step/1',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/1` as const,
  }),
  composeRoute({
    id: 'configuration/step-1/history',
    route: '/configuration/:configurationId/step/1/history/:process?',
    make: ({ configuration, manCode, processId }: ConfigurationParams & { processId?: OptionChangeId }) =>
      processId
        ? (`/configuration/${configId(configuration, manCode)}/step/1/history/${processId}` as const)
        : (`/configuration/${configId(configuration, manCode)}/step/1/history` as const),
  }),

  // step-2
  composeRoute({
    id: 'configuration/step-2',
    route: '/configuration/:configurationId/step/2',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/2` as const,
  }),
  composeRoute({
    id: 'configuration/step-2/history',
    route: '/configuration/:configurationId/step/2/history/:process?',
    make: ({ configuration, manCode, processId }: ConfigurationParams & { processId?: OptionChangeId }) =>
      processId
        ? (`/configuration/${configId(configuration, manCode)}/step/2/history/${processId}` as const)
        : (`/configuration/${configId(configuration, manCode)}/step/2/history` as const),
  }),
  composeRoute({
    id: 'configuration/step-2/compare',
    route: '/configuration/:configurationId/step/2/compare/:otherConfigurationId?',
    make: ({
      configuration,
      manCode,
      otherConfiguration,
      otherConfigurationManCode,
    }: ConfigurationParams & { otherConfiguration?: TruckConfiguration | LocalConfigurationId; otherConfigurationManCode?: ManCodeWithSortOrder }) =>
      otherConfiguration
        ? (`/configuration/${configId(configuration, manCode)}/step/2/compare/${configId(otherConfiguration, otherConfigurationManCode)}` as const)
        : (`/configuration/${configId(configuration, manCode)}/step/2/compare` as const),
  }),
  composeRoute({
    id: 'configuration/step-2/print',
    route: '/configuration/:configurationId/step/2/print',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/2/print` as const,
  }),
  composeRoute({
    id: 'configuration/step-2/build-verification',
    route: '/configuration/:configurationId/step/2/build-verification/:taskId?',
    make: ({ configuration, manCode, taskId }: ConfigurationParams & { taskId?: CheckTaskId }) =>
      taskId
        ? (`/configuration/${configId(configuration, manCode)}/step/2/build-verification/${taskId}` as const)
        : (`/configuration/${configId(configuration, manCode)}/step/2/build-verification` as const),
  }),
  composeRoute({
    id: 'configuration/step-2/technical-data',
    route: '/configuration/:configurationId/step/2/technical-data',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/2/technical-data` as const,
  }),
  composeRoute({
    id: 'configuration/step-2/optional-equipment',
    route: '/configuration/:configurationId/step/2/optional-equipment',
    make: ({ configuration, manCode }: ConfigurationParams) =>
      `/configuration/${configId(configuration, manCode)}/step/2/optional-equipment` as const,
  }),
  composeRoute({
    id: 'configuration/step-2/tire-editor',
    route: '/configuration/:configurationId/step/2/tire-editor',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/2/tire-editor` as const,
  }),

  // step-3
  composeRoute({
    id: 'configuration/step-3',
    route: '/configuration/:configurationId/step/3',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/3` as const,
  }),
  composeRoute({
    id: 'configuration/step-3/history',
    route: '/configuration/:configurationId/step/3/history',
    make: ({ configuration, manCode, processId }: ConfigurationParams & { processId?: OptionChangeId }) =>
      processId
        ? (`/configuration/${configId(configuration, manCode)}/step/3/history/${processId}` as const)
        : (`/configuration/${configId(configuration, manCode)}/step/3/history` as const),
  }),
  composeRoute({
    id: 'configuration/step-3/changes',
    route: '/configuration/:configurationId/step/3/changes',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/3/changes` as const,
  }),
  composeRoute({
    id: 'configuration/step-3/compare',
    route: '/configuration/:configurationId/step/3/compare/:otherConfiguration?',
    make: ({
      configuration,
      manCode,
      otherConfiguration,
      otherManCode,
    }: ConfigurationParams & { otherConfiguration?: TruckConfiguration | LocalConfigurationId; otherManCode?: ManCodeWithSortOrder }) =>
      otherConfiguration
        ? `/configuration/${configId(configuration, manCode)}/step/3/compare/${configId(otherConfiguration, otherManCode)}`
        : `/configuration/${configId(configuration, manCode)}/step/3/compare`,
  }),
  composeRoute({
    id: 'configuration/step-3/technical-data',
    route: '/configuration/:configurationId/step/3/technical-data',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/3/technical-data` as const,
  }),
  composeRoute({
    id: 'configuration/step-3/build-verification',
    route: '/configuration/:configurationId/step/3/build-verification/:taskId?',
    make: ({ configuration, manCode, taskId }: ConfigurationParams & { taskId?: CheckTaskId }) =>
      taskId
        ? (`/configuration/${configId(configuration, manCode)}/step/3/build-verification/${taskId}` as const)
        : (`/configuration/${configId(configuration, manCode)}/step/3/build-verification` as const),
  }),
  composeRoute({
    id: 'configuration/step-3/optional-equipment',
    route: '/configuration/:configurationId/step/3/optional-equipment',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/3/optional-equipment`,
  }),
  composeRoute({
    id: 'configuration/step-3/submitted',
    route: '/configuration/:configurationId/step/3/submitted',
    make: ({ configuration, manCode }: ConfigurationParams) => `/configuration/${configId(configuration, manCode)}/step/3/submitted`,
  }),
] as const
